import { useTranslation } from 'react-i18next';
import formatHelper from '@knack/format-helper';

import { type KnackFieldKey } from '@/types/schema/KnackField';
import { type KnackObjectResponse } from '@/types/schema/KnackObject';
import {
  type PivotTableView,
  type PivotTableViewColumn
} from '@/types/schema/views/PivotTableView';
import {
  type ChartCalculation,
  type ChartCalculationKey,
  type ChartRecord,
  type ChartData as pivotTableData
} from '@/types/schema/views/ReportView';
import { type PagedViewRecords } from '@/hooks/api/queries/useViewMultipleRecordsQuery';
import { type FormattedViewRecord } from '@/hooks/api/queries/useViewRecordQuery';
import {
  getSummaryFieldFormat,
  replaceNbsp
} from '@/components/views/common/table/summaries/helper';
import { type ChartCalculationByKey } from '@/components/views/report/shared/useChart';
import {
  CALCULATION_PREFIX,
  createBaseColumn,
  getCalculationRawFields,
  initializeRecordWithFields,
  isCalculationField,
  isCalculationOrRawField,
  LIMIT_BEFORE_GROUPING,
  processCalculationOrRawValues,
  processCalculationValues,
  sumNumericFields
} from './pivotTableUtils';

type FormatLabelValueParams = {
  value: number | string;
  chartCalculationByKeys: ChartCalculationByKey;
  calculationKey: ChartCalculationKey | undefined;
  sourceTable: KnackObjectResponse | null;
};

export function usePivotTableHelpers() {
  const [t] = useTranslation();

  const aggregateData = (data: ChartRecord[]): ChartRecord[] => {
    const aggregated = new Map<string, ChartRecord>();

    data.forEach((item) => {
      const group = String(item.group_0 || t('components.views.report.others'));

      // If the group doesn't exist, create it with the basic structure
      if (!aggregated.has(group)) {
        aggregated.set(group, { group_0: group });
      }

      const currentValue = aggregated.get(group)!;

      Object.keys(item).forEach((key) => {
        if (isCalculationOrRawField(key)) {
          // If the field doesn't exist in the accumulated value, initialize it to 0
          if (!currentValue[key]) {
            currentValue[key] = 0;
          }

          if (typeof currentValue[key] === 'number' && typeof item[key] === 'number') {
            currentValue[key] = (currentValue[key] as number) + (item[key] as number);
          }
        }
      });
    });

    return Array.from(aggregated.values());
  };

  // Limits the records to a certain number of items
  const limitRecords = (data: ChartRecord[], limit = LIMIT_BEFORE_GROUPING): ChartRecord[] => {
    if (data.length <= limit) {
      return data;
    }

    const sorted = [...data].sort((a, b) => {
      const valA = typeof a.calc_0 === 'number' ? (a.calc_0 as number) : 0;
      const valB = typeof b.calc_0 === 'number' ? (b.calc_0 as number) : 0;
      return valB - valA;
    });

    const limited = sorted.slice(0, limit);
    const calcRawFields = getCalculationRawFields(sorted);

    const initialOthers = initializeRecordWithFields(
      { group_0: t('components.views.report.others') },
      calcRawFields
    );

    const others = sorted
      .slice(limit)
      .reduce((acc, item) => sumNumericFields(acc, item, calcRawFields), initialOthers);

    return [...limited, others];
  };

  // Get the calculation key with the maximum value. It's useful to set the YAxis domain
  const getMaxValueCalculation = (
    chartCalculations: ChartCalculation[],
    calculationRecords: ChartRecord[]
  ): ChartCalculationKey => {
    let maxCalculationKey: ChartCalculationKey = `${CALCULATION_PREFIX}0`;
    let maxCalculationValue = 0;

    chartCalculations.forEach((calculation, index) => {
      const calculationKey = `${CALCULATION_PREFIX}${index}` as ChartCalculationKey;
      const calculationValues = calculationRecords
        .map((record) => record[calculationKey])
        .filter((value): value is number => typeof value === 'number');

      if (calculationValues.length === 0) return;

      const calculationMaxValue = Math.max(...calculationValues);

      if (calculationMaxValue > maxCalculationValue) {
        maxCalculationKey = calculationKey;
        maxCalculationValue = calculationMaxValue;
      }
    });

    return maxCalculationKey;
  };

  const getRecordsInPercentage = (records: ChartRecord[]): ChartRecord[] =>
    records.map((record) => {
      const calculationValues = Object.keys(record).filter(
        (key) => typeof record[key] === 'number' && isCalculationField(key)
      );

      const totalSum = calculationValues.reduce((acc, key) => acc + (record[key] as number), 0);

      if (totalSum === 0) return record;

      return processCalculationValues(record, (value) => ((value / totalSum) * 100).toFixed(2));
    });

  const transformNegativeValuesToZero = (records: ChartRecord[]): ChartRecord[] =>
    records.map((record) => processCalculationValues(record, (value) => (value < 0 ? 0 : value)));

  const limitDecimalPlaces = (chartRecords: ChartRecord[]): ChartRecord[] => {
    if (!chartRecords.length) return chartRecords;

    return chartRecords.map((record) =>
      processCalculationOrRawValues(record, (value) => parseFloat(Number(value).toFixed(2)))
    );
  };

  const filterEmptyGroups = (records: ChartRecord[]): ChartRecord[] =>
    records.filter((record) => {
      const calculationValues = Object.keys(record).filter(
        (key) => typeof record[key] === 'number' && isCalculationField(key)
      );

      return calculationValues.some((key) => {
        const value = record[key];
        return value !== null && value !== 0;
      });
    });

  const hasValidData = (
    chartData: pivotTableData | undefined,
    chartRecords: ChartRecord[]
  ): boolean => Boolean(chartData?.records?.length && chartRecords.length);

  // This function is responsible for formatting the value if the field is of type currency or number,
  // with the format applied in the field
  const formatLabelValue = ({
    value,
    chartCalculationByKeys,
    calculationKey,
    sourceTable
  }: FormatLabelValueParams): string => {
    if (!calculationKey || typeof value !== 'number') {
      return replaceNbsp(value.toString());
    }

    const currentChartCalculation = chartCalculationByKeys[calculationKey];

    const calculationField = sourceTable?.fields.find(
      (f) => f.key === currentChartCalculation.field
    );

    const validFieldTypes = ['number', 'currency'];
    if (!calculationField || !validFieldTypes.includes(calculationField.type)) {
      return value.toString();
    }

    // If we are working with currency fields, it is possible that the value comes as 13&nbsp;€
    return replaceNbsp(formatHelper.formatNumber(value, getSummaryFieldFormat(calculationField)));
  };

  function getCalculationColumns(
    view: PivotTableView,
    chartData: pivotTableData
  ): PivotTableViewColumn[] {
    const createColumn = (
      id: string,
      header: string,
      fieldKey: KnackFieldKey
    ): PivotTableViewColumn => ({
      id,
      header,
      field: { key: fieldKey },
      group_sort: 'asc',
      align: 'right',
      pivot_column_type: 'calculation',
      ...createBaseColumn()
    });

    if (view.groups.some((group) => group.pivot)) {
      const pivotColumnsHeaders = Array.isArray(chartData?.pivot_cols) ? chartData.pivot_cols : [];

      return pivotColumnsHeaders.map((header, index) =>
        createColumn(`calc_${index}`, header, `pivot_col_${index}` as KnackFieldKey)
      );
    }

    return view.calculations.map((calc, index) =>
      createColumn(`calc_${index}`, calc.label, calc.field as KnackFieldKey)
    );
  }

  function generatePivotTableColumns(
    pivotTableData: pivotTableData,
    view: PivotTableView
  ): PivotTableViewColumn[] {
    const groupColumns = view.groups.reduce<PivotTableViewColumn[]>((acc, group, index) => {
      if (!group.pivot) {
        acc.push({
          id: `group_${index}`,
          header: group.label,
          field: { key: group.field },
          group_sort: group.sort || 'asc',
          align: 'left',
          pivot_column_type: 'group',
          ...createBaseColumn()
        });
      }
      return acc;
    }, []);

    return [...groupColumns, ...getCalculationColumns(view, pivotTableData)];
  }

  function transformRecords(
    pivotTableData: pivotTableData | undefined,
    view: PivotTableView
  ): PagedViewRecords {
    const emptyResult = {
      records: [],
      totalRecords: 0,
      totalPages: 1,
      currentPage: 1,
      isSearchTermValid: false
    };

    if (!pivotTableData?.records.length) return emptyResult;

    const transformedRecords = pivotTableData.records.map((record: ChartRecord, recordIndex) => {
      const newRecord: FormattedViewRecord = { values: {}, rawValues: {} };
      const recordId = `record_${recordIndex}`;

      view.columns.forEach((column, index) => {
        const fieldKey = column.field.key;
        const value = record[column.id];
        const rawValue =
          column.pivot_column_type === 'group' ? record[`raw_${index}`] : record[column.id];

        newRecord.values[fieldKey] = value;
        newRecord.rawValues[fieldKey] = rawValue;
      });

      newRecord.values.id = recordId;
      newRecord.rawValues.id = recordId;
      return newRecord;
    });

    return {
      ...emptyResult,
      records: transformedRecords,
      totalRecords: transformedRecords.length
    };
  }

  return {
    aggregateData,
    limitRecords,
    filterEmptyGroups,
    getMaxValueCalculation,
    getRecordsInPercentage,
    transformNegativeValuesToZero,
    limitDecimalPlaces,
    hasValidData,
    formatLabelValue,
    generatePivotTableColumns,
    transformRecords
  };
}
